// videoExport.js — "download with the look baked in" for imported screen
// recordings.
//
// The recorder look (wallpaper + padding + rounded window + shadow + browser
// bar + chrome/taskbar crop) and the auto-zoom camera are playback-time
// overlays in EnrichedPlayer (videoZoom.js) — the clip itself is raw. To hand
// the user a standalone file we replay the clip into a <canvas>, paint the SAME
// geometry (videoLookGeometry + cardZoomTransform) for every frame of a fixed
// 30 fps grid, encode with WebCodecs (VP9, VP8 fallback; Opus audio) and mux
// with webmMuxer.js — which also writes Duration + Cues, so the file seeks
// properly (MediaRecorder WebM has neither, and MediaRecorder dropped the tail
// whenever its real-time encoder fell behind a 1080p canvas).

import {
  sampleZoom,
  cardZoomTransform,
  videoLookGeometry,
  frameThemeColors,
  LOOK_REF_W,
  VIDEO_SHADOW,
} from "./videoZoom";
import { createWebmMuxer } from "./webmMuxer";

// ── CSS background → canvas paint ───────────────────────────────────────────
// The recorder's backgrounds are a solid colour or a CSS linear-/radial-
// gradient string (video-style.ts presets). Canvas can't take CSS, so parse
// that small subset; anything unrecognised falls back to its first colour.

// Split on top-level commas (commas inside rgb(...) stay put).
function splitTopLevel(str) {
  const out = [];
  let depth = 0;
  let cur = "";
  for (const ch of str) {
    if (ch === "(") depth++;
    else if (ch === ")") depth--;
    if (ch === "," && depth === 0) {
      out.push(cur.trim());
      cur = "";
    } else cur += ch;
  }
  if (cur.trim()) out.push(cur.trim());
  return out;
}

// "#fff 30%" | "rgb(1,2,3)" → { color, pos (0..1 | null) }
function parseStop(s) {
  const m = s.match(/^(.*?)(?:\s+(-?[\d.]+)%)?$/);
  return { color: m[1].trim(), pos: m[2] != null ? parseFloat(m[2]) / 100 : null };
}

function addStops(grad, stops) {
  const n = stops.length;
  stops.forEach((st, i) => {
    const pos = st.pos != null ? st.pos : n === 1 ? 0 : i / (n - 1);
    try {
      grad.addColorStop(Math.max(0, Math.min(1, pos)), st.color);
    } catch (_) {
      /* unparsable colour — skip the stop */
    }
  });
}

/** Exported for tests: CSS background → canvas fillStyle for a w×h rect at (x,y). */
export function cssBackgroundToFill(ctx, css, x, y, w, h) {
  const bg = (css || "").trim();
  const lin = bg.match(/^linear-gradient\((.*)\)$/i);
  const rad = bg.match(/^radial-gradient\((.*)\)$/i);
  if (lin) {
    const parts = splitTopLevel(lin[1]);
    let angle = 180; // CSS default: to bottom
    if (/^-?[\d.]+deg$/i.test(parts[0])) angle = parseFloat(parts.shift());
    const a = (angle * Math.PI) / 180;
    const dx = Math.sin(a);
    const dy = -Math.cos(a);
    const len = Math.abs(w * dx) + Math.abs(h * dy); // CSS gradient-line length
    const cx = x + w / 2;
    const cy = y + h / 2;
    const g = ctx.createLinearGradient(
      cx - (dx * len) / 2,
      cy - (dy * len) / 2,
      cx + (dx * len) / 2,
      cy + (dy * len) / 2
    );
    addStops(g, parts.map(parseStop));
    return g;
  }
  if (rad) {
    const parts = splitTopLevel(rad[1]);
    let px = 0.5;
    let py = 0.5;
    const at = parts[0].match(/at\s+(-?[\d.]+)%\s+(-?[\d.]+)%/i);
    if (/circle|ellipse|at\s/i.test(parts[0])) parts.shift();
    if (at) {
      px = parseFloat(at[1]) / 100;
      py = parseFloat(at[2]) / 100;
    }
    const cx = x + w * px;
    const cy = y + h * py;
    // CSS default size: farthest-corner.
    const r = Math.max(
      Math.hypot(cx - x, cy - y),
      Math.hypot(cx - (x + w), cy - y),
      Math.hypot(cx - x, cy - (y + h)),
      Math.hypot(cx - (x + w), cy - (y + h))
    );
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    addStops(g, parts.map(parseStop));
    return g;
  }
  return bg || "#000";
}

// ── Layout in output pixels ────────────────────────────────────────────────

/**
 * Pixel layout of the composed frame for an output `outW` px wide. Same
 * geometry as the on-canvas preview (videoLookGeometry is in frame-width units).
 */
export function exportLayout(geo, outW) {
  const W = outW;
  const H = Math.round(W / geo.aspect / 2) * 2; // even for the encoder
  const winX = geo.pad * W;
  const winY = (H - geo.winH * W) / 2;
  const winW = geo.winW * W;
  const winH = geo.winH * W;
  const barH = geo.barH * W;
  return {
    W,
    H,
    u: W / LOOK_REF_W, // recorder px → output px
    win: { x: winX, y: winY, w: winW, h: winH, r: geo.radius * W },
    outerR: geo.outerRadius * W,
    barH,
    stage: { x: winX, y: winY + barH, w: winW, h: winH - barH },
  };
}

function roundRectPath(ctx, x, y, w, h, r) {
  ctx.beginPath();
  if (r > 0 && typeof ctx.roundRect === "function") ctx.roundRect(x, y, w, h, r);
  else ctx.rect(x, y, w, h);
}

// Wallpaper (static every frame — painted once into a layer). Black outside
// the rounded outer corners; VP9 has no alpha.
function paintBackgroundLayer(geo, L) {
  const c = document.createElement("canvas");
  c.width = L.W;
  c.height = L.H;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, L.W, L.H);
  if (geo.background) {
    ctx.save();
    roundRectPath(ctx, 0, 0, L.W, L.H, L.outerR);
    ctx.clip();
    ctx.fillStyle = cssBackgroundToFill(ctx, geo.background, 0, 0, L.W, L.H);
    ctx.fillRect(0, 0, L.W, L.H);
    ctx.restore();
  }
  return c;
}

// Ellipsise the address once (measured at the card's own scale).
function fitPillText(ctx, text, maxW) {
  let t = text || "";
  while (t.length > 1 && ctx.measureText(t).width > maxW) t = t.slice(0, -2) + "…";
  return t;
}

/**
 * Paint the card (shadow + window + bar + cropped video) in CARD-LOCAL px —
 * the caller has already applied the card-fly transform (origin = card
 * top-left). Canvas shadows ignore the transform matrix, so `scale` is passed
 * in to size the shadow like the CSS one (which scales with the card).
 */
function paintCard(ctx, geo, L, video, fullH, scale, pill) {
  const w = L.win.w;
  const h = L.win.h;
  const r = L.win.r;
  const u = L.u;
  ctx.save();
  if (geo.shadow) {
    ctx.shadowColor = VIDEO_SHADOW.color;
    ctx.shadowBlur = VIDEO_SHADOW.blur * u * scale;
    ctx.shadowOffsetY = VIDEO_SHADOW.y * u * scale;
  }
  ctx.fillStyle = "#000";
  roundRectPath(ctx, 0, 0, w, h, r);
  ctx.fill();
  ctx.restore();

  ctx.save();
  roundRectPath(ctx, 0, 0, w, h, r);
  ctx.clip();
  // Video, shifted up by the top crop; the bar then paints over the top band.
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, L.barH, w, h - L.barH);
  ctx.clip();
  // Contain-fit the CURRENT frame into the full-video rect, in case the clip
  // changes resolution mid-stream (never stretch it).
  const fw = video.videoWidth || w;
  const fh = video.videoHeight || fullH;
  const k = Math.min(w / fw, fullH / fh);
  const dw = fw * k;
  const dh = fh * k;
  ctx.fillStyle = "#000";
  ctx.fillRect(0, L.barH, w, h - L.barH);
  ctx.drawImage(video, (w - dw) / 2, L.barH - geo.ct * fullH + (fullH - dh) / 2, dw, dh);
  ctx.restore();

  const f = geo.frame;
  if (f && f.bar) {
    const th = frameThemeColors(f.theme);
    ctx.fillStyle = th.bar;
    ctx.fillRect(0, 0, w, L.barH);
    const cy = L.barH / 2;
    let x = 12 * u;
    for (const col of ["#ff5f57", "#febc2e", "#28c840"]) {
      ctx.fillStyle = col;
      ctx.beginPath();
      ctx.arc(x + 5.5 * u, cy, 5.5 * u, 0, Math.PI * 2);
      ctx.fill();
      x += 19 * u; // 11px dot + 8px gap
    }
    ctx.font = pill.font;
    const pillH = 20 * u;
    const pillX = Math.max(x, (w - pill.w) / 2);
    ctx.fillStyle = th.pill;
    roundRectPath(ctx, pillX, cy - pillH / 2, pill.w, pillH, 6 * u);
    ctx.fill();
    ctx.fillStyle = th.pillText;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(pill.text, pillX + pill.w / 2, cy);
    if (f.border) {
      ctx.strokeStyle = th.border;
      ctx.lineWidth = Math.max(1, u);
      roundRectPath(ctx, 0.5, 0.5, w - 1, h - 1, r);
      ctx.stroke();
    }
  }
  ctx.restore();
}

// ── Media helpers ──────────────────────────────────────────────────────────

function once(el, events, timeoutMs) {
  return new Promise((resolve, reject) => {
    const evs = Array.isArray(events) ? events : [events];
    let timer = null;
    const done = (fn, v) => {
      evs.forEach((e) => el.removeEventListener(e, onEv));
      el.removeEventListener("error", onErr);
      if (timer) clearTimeout(timer);
      fn(v);
    };
    const onEv = (e) => done(resolve, e);
    const onErr = () => done(reject, new Error("video-load-failed"));
    evs.forEach((e) => el.addEventListener(e, onEv));
    el.addEventListener("error", onErr);
    if (timeoutMs) timer = setTimeout(() => done(resolve, null), timeoutMs);
  });
}

// MediaRecorder WebM reports duration=Infinity until it has been seeked to the
// end once — the recorder's proven seek trick.
async function resolveDuration(v) {
  if (Number.isFinite(v.duration) && v.duration > 0) return v.duration;
  v.currentTime = 1e101;
  await once(v, ["durationchange", "seeked"], 4000);
  const d = Number.isFinite(v.duration) ? v.duration : 0;
  v.currentTime = 0;
  await once(v, "seeked", 4000);
  return d;
}

// Seek to `t` and read the frame size once that frame is presented.
async function settleDims(v, t) {
  v.currentTime = Math.max(0, t);
  await once(v, "seeked", 4000);
  await new Promise((r) => setTimeout(r, 150));
  return [v.videoWidth, v.videoHeight];
}

const FPS = 30;
const VIDEO_BITRATE = 12_000_000; // screen content: low bitrates smear text
const KEYFRAME_EVERY = FPS * 2; // also the Cues/seek granularity
const MAX_ENCODE_QUEUE = 6; // pause the source while the encoder catches up

async function pickVideoCodec(width, height) {
  if (typeof VideoEncoder === "undefined") return null;
  const cands = [
    ["vp09.00.40.08", "V_VP9"],
    ["vp8", "V_VP8"],
  ];
  for (const [codec, mkv] of cands) {
    const cfg = { codec, width, height, bitrate: VIDEO_BITRATE, framerate: FPS };
    try {
      const r = await VideoEncoder.isConfigSupported(cfg);
      if (r && r.supported) return { cfg, mkv };
    } catch (_) {
      /* try the next codec */
    }
  }
  return null;
}

// The clip's own audio track (screen recordings may carry mic/tab audio),
// decoded at 48 kHz for Opus. null when there is none / it can't be decoded.
async function decodeClipAudio(blob) {
  if (typeof AudioEncoder === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  let ac = null;
  try {
    ac = new AC({ sampleRate: 48000 });
    return await ac.decodeAudioData(await blob.arrayBuffer());
  } catch (_) {
    return null;
  } finally {
    if (ac) ac.close().catch(() => {});
  }
}

async function encodeAudio(buf, startSec, endSec, muxer) {
  const sr = buf.sampleRate;
  const ch = Math.min(2, buf.numberOfChannels);
  const cfg = { codec: "opus", sampleRate: sr, numberOfChannels: ch, bitrate: 128000 };
  const sup = await AudioEncoder.isConfigSupported(cfg).catch(() => null);
  if (!sup || !sup.supported) return false;
  let err = null;
  const enc = new AudioEncoder({
    output: (c) => muxer.addAudioChunk(c),
    error: (e) => (err = e),
  });
  enc.configure(cfg);
  const s0 = Math.floor(startSec * sr);
  const s1 = Math.min(buf.length, Math.floor(endSec * sr));
  const STEP = sr; // 1 s of samples per AudioData
  for (let i = s0; i < s1 && !err; i += STEP) {
    const n = Math.min(STEP, s1 - i);
    const data = new Float32Array(n * ch);
    for (let c = 0; c < ch; c++) data.set(buf.getChannelData(c).subarray(i, i + n), c * n);
    const ad = new AudioData({
      format: "f32-planar",
      sampleRate: sr,
      numberOfFrames: n,
      numberOfChannels: ch,
      timestamp: Math.round(((i - s0) / sr) * 1e6),
      data,
    });
    enc.encode(ad);
    ad.close();
  }
  await enc.flush();
  enc.close();
  if (err) throw err;
  return true;
}

/**
 * Render the box's video with its look + auto-zoom baked in.
 *
 * Frames are produced on a fixed 30 fps MEDIA-time grid while the (muted)
 * source plays, and encoded with WebCodecs — timestamps come from the grid,
 * not the wall clock, so whenever the encoder falls behind, the source is
 * simply paused until it catches up: no frame is ever dropped, on any GPU.
 * The tab should stay in front (a hidden tab pauses the render).
 *
 * @param {object} opts
 * @param {string} opts.url     video URL (fetched to a blob so the canvas stays untainted)
 * @param {object} opts.state   EnrichedPlayer box state (zoomKeyframes, videoFrame, videoWallpaper)
 * @param {number} [opts.startSec] / [opts.endSec]  optional trim window
 * @param {number} [opts.maxWidth=1920]
 * @param {boolean} [opts.includeAudio=true]  false = export without the clip's audio
 * @param {(p:number)=>void} [opts.onProgress]  0..1
 * @param {AbortSignal} [opts.signal]
 * @returns {Promise<Blob>} video/webm (with Duration + Cues — seekable)
 */
export async function exportStyledVideo(opts) {
  const { url, state, onProgress, signal } = opts;
  const maxWidth = opts.maxWidth || 1920;
  if (typeof VideoEncoder === "undefined" || typeof VideoFrame === "undefined") {
    throw new Error("webcodecs-unsupported");
  }

  const resp = await fetch(url, { signal });
  if (!resp.ok) throw new Error(`video-fetch-failed-${resp.status}`);
  const srcBlob = await resp.blob();
  const srcUrl = URL.createObjectURL(srcBlob);

  const v = document.createElement("video");
  v.playsInline = true;
  v.muted = true; // audio is decoded separately; muted playback never needs a gesture
  v.preload = "auto";
  v.src = srcUrl;
  let raf = null;
  let venc = null;
  let onVis = null;
  try {
    await once(v, "loadedmetadata");
    const fullDur = await resolveDuration(v);
    const startSec = Math.max(0, opts.startSec || 0);
    const endSec =
      opts.endSec != null && opts.endSec > startSec
        ? Math.min(opts.endSec, fullDur || Infinity)
        : fullDur;
    if (!(endSec > startSec)) throw new Error("video-no-duration");
    // A tab capture's FIRST frame often has a different size than the rest
    // (loadedmetadata reports it) — size the layout from a frame a little in.
    const [vw, vh] = await settleDims(v, Math.min(startSec + 0.5, (startSec + endSec) / 2));
    if (!vw || !vh) throw new Error("video-no-dimensions");

    const ratio = vw / vh;
    // No look on the box → still bake the zoom onto a plain full-frame canvas.
    const geo =
      videoLookGeometry(state, ratio) ||
      videoLookGeometry({ videoFrame: { cropTop: 0, cropBottom: 0, bar: false } }, ratio);
    // Size so the stage keeps the clip's native resolution, capped at maxWidth.
    const outW = Math.min(maxWidth, Math.round(vw / geo.winW / 2) * 2);
    const L = exportLayout(geo, outW);
    const bgLayer = paintBackgroundLayer(geo, L);

    const codec = await pickVideoCodec(L.W, L.H);
    if (!codec) throw new Error("video-encoder-unsupported");

    const canvas = document.createElement("canvas");
    canvas.width = L.W;
    canvas.height = L.H;
    const ctx = canvas.getContext("2d");
    const kfs = Array.isArray(state && state.zoomKeyframes) ? state.zoomKeyframes : null;
    const fullH = L.stage.w / ratio; // the un-cropped video's drawn height

    // Address pill (minimal frame): font + ellipsised text fixed up front.
    const pill = {
      font: `${12 * L.u}px -apple-system, "Segoe UI", Roboto, sans-serif`,
      text: "",
      w: 0,
    };
    if (geo.frame && geo.frame.bar) {
      ctx.font = pill.font;
      const padX = 10 * L.u;
      const maxW = Math.min(0.7 * L.win.w, L.win.w - 2 * 12 * L.u - 3 * 19 * L.u);
      pill.text = fitPillText(ctx, geo.frame.url, maxW - 2 * padX);
      pill.w = Math.min(maxW, ctx.measureText(pill.text).width + 2 * padX);
    }
    // Card-fly geometry (videoZoom.js cardZoomTransform), in output px.
    const cardGeom = {
      Wv: L.win.w,
      Hv: fullH,
      vx: 0,
      vy: L.barH - geo.ct * fullH,
      Wc: L.win.w,
      Hc: L.win.h,
      Lx: L.win.x,
      Ly: L.win.y,
      stageW: L.W,
      stageH: L.H,
    };

    // Paint the frame for media time tSec (the camera is sampled on the grid
    // time itself, so the zoom is exact regardless of playback hiccups).
    const draw = (tSec) => {
      const s = kfs ? sampleZoom(kfs, tSec * 1000) : { scale: 1, focusX: 0.5, focusY: 0.5 };
      const z = cardZoomTransform(s.scale, s.focusX, s.focusY, cardGeom, 0);
      ctx.drawImage(bgLayer, 0, 0);
      ctx.save();
      roundRectPath(ctx, 0, 0, L.W, L.H, L.outerR);
      ctx.clip();
      ctx.translate(L.win.x + z.tx, L.win.y + z.ty);
      ctx.scale(z.scale, z.scale);
      paintCard(ctx, geo, L, v, fullH, z.scale, pill);
      ctx.restore();
    };

    // includeAudio:false → silent export (no audio track at all).
    const audioBuf = opts.includeAudio === false ? null : await decodeClipAudio(srcBlob);
    const muxer = createWebmMuxer({
      video: { codec: codec.mkv, width: L.W, height: L.H },
      audio: audioBuf
        ? { sampleRate: audioBuf.sampleRate, channels: Math.min(2, audioBuf.numberOfChannels) }
        : null,
    });

    let encErr = null;
    venc = new VideoEncoder({
      output: (chunk) => muxer.addVideoChunk(chunk),
      error: (e) => (encErr = e),
    });
    venc.configure({ ...codec.cfg, latencyMode: "quality" });

    const total = Math.max(1, Math.round((endSec - startSec) * FPS));
    let i = 0;
    const emit = () => {
      draw(startSec + i / FPS);
      const frame = new VideoFrame(canvas, {
        timestamp: Math.round((i * 1e6) / FPS),
        duration: Math.round(1e6 / FPS),
      });
      venc.encode(frame, { keyFrame: i % KEYFRAME_EVERY === 0 });
      frame.close();
      i++;
    };

    v.currentTime = startSec;
    await once(v, "seeked", 4000);

    // A hidden tab stops rAF; pause the source so frames aren't skipped.
    onVis = () => {
      if (document.hidden) v.pause();
    };
    document.addEventListener("visibilitychange", onVis);

    await new Promise((resolve, reject) => {
      const loop = () => {
        if (signal && signal.aborted) return reject(new DOMException("Aborted", "AbortError"));
        if (encErr) return reject(encErr);
        if (venc.encodeQueueSize > MAX_ENCODE_QUEUE) {
          if (!v.paused) v.pause(); // back-pressure: let the encoder catch up
        } else {
          const ended = v.ended || v.currentTime >= endSec;
          const ct = ended ? endSec : v.currentTime;
          // Every grid frame the playhead has reached (≤ 1 per rAF normally).
          while (
            i < total &&
            startSec + i / FPS <= ct &&
            venc.encodeQueueSize <= MAX_ENCODE_QUEUE
          ) {
            emit();
          }
          if (onProgress) onProgress(Math.min(1, i / total));
          if (i >= total) return resolve();
          if (!ended && v.paused && !document.hidden) {
            v.play().catch((e) => reject(e));
          }
        }
        raf = requestAnimationFrame(loop);
      };
      v.addEventListener("error", () => reject(new Error("video-playback-failed")), {
        once: true,
      });
      raf = requestAnimationFrame(loop);
    });
    v.pause();
    await venc.flush();
    if (encErr) throw encErr;

    if (audioBuf) await encodeAudio(audioBuf, startSec, endSec, muxer);
    if (onProgress) onProgress(1);
    return muxer.finalize((endSec - startSec) * 1000);
  } finally {
    if (raf) cancelAnimationFrame(raf);
    if (onVis) document.removeEventListener("visibilitychange", onVis);
    if (venc && venc.state !== "closed") {
      try {
        venc.close();
      } catch (_) {}
    }
    v.pause();
    v.removeAttribute("src");
    v.load();
    URL.revokeObjectURL(srcUrl);
  }
}

/** Save a blob under `name` via a temporary <a download>. */
export function downloadBlob(blob, name) {
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}
